<template>
  <div class="relative flex flex-col h-screen">
    <img :src="getImage('mascot')" class="fixed papsi-mascot" />

    <div class="flex flex-1 lg:flex-row flex-col">
      <SidebarSection>
        <LabelControl type="message" :content="$t('asn001')" />
      </SidebarSection>

      <ContentSection :show_logout="false">
        <div>
          <LabelControl class="mb-12" type="heading" :content="$t('asn002')" />

          <div class="flex" style="gap: 1rem;">
            <button v-for="option in tab_options" :key="option.value" @click="setSelectedTab(option.value)" :class="[
              'px-6 py-4 text-4xl font-medium focus:outline-none',
              selected_input_type === option.value
                ? 'bg-primary text-white rounded-t-2xl'
                : 'bg-grey text-white rounded-t-2xl'
            ]">
              {{ option.label }}
            </button>
          </div>

          <!-- Input Section -->
          <div class="bg-grey-lighter p-10 flex flex-col rounded-b-2xl rounded-tr-2xl papsi-container-acct-serial">
            <div @click="openKeyboard(selected_input_type)"
              class="bg-white rounded-xl px-8 py-6 text-4xl text-grey-dark cursor-pointer">
              <span :class="input_value ? 'text-black' : 'text-grey-dark'">
                {{ input_value || `${$t('asn003')} ${selected_input_type === 'account' ? 'Account Number' : 'Serial Number'}` }}
              </span>
            </div>
          </div>

          <!-- Proceed Button -->
          <!-- <div class="flex flex-row justify-end">
            <ButtonControl :disabled="isButtonDisabled" @click="handleProceed" theme="success" :text="$t('btn002')" />
          </div> -->
        </div>
      </ContentSection>
    </div>

    <FooterSection />

    <!-- Keyboard Component -->
    <transition name="slide-in">
      <KeyboardControl v-if="keyboard_visible" :password="false" @submit="updateField" :is_caps="true"
        :is_account="selected_input_type === 'account'" :selected_input_type="selected_input_type"
        :inputted_value="input_value" :max_length="max_lengths[selected_input_type]">
        <div class="flex flex-row items-center justify-between mb-10 w-full">
          <h2 class="text-primary uppercase text-5xl font-bold">
            {{ selected_input_type === 'account' ? $t('asn004') : $t('asn005') }}
          </h2>
          <IconControl class="exit-icon" @click="closeKeyboard" id="exit-black" :width="50" :height="50" />
        </div>
      </KeyboardControl>
    </transition>
  </div>
</template>


<script>
import SidebarSection from '@/components/sections/SidebarSection.vue';
import ContentSection from '@/components/sections/ContentSection.vue';
import FooterSection from '@/components/sections/FooterSection.vue';
import LabelControl from '@/components/controls/LabelControl.vue';
import IconControl from '@/components/controls/IconControl.vue';
import mixin from '@/mixins/controls.js';
import KeyboardControl from '@/components/controls/KeyboardControl.vue';
import user from '@/services/user';

export default {
  mixins: [mixin],
  mounted() {
    this.$emit('checkauth', false);
    this.startInactivityTimer();
    window.addEventListener("touchstart", this.resetInactivityTimer);
  },
  beforeUnmount() {
    clearTimeout(this.inactivity_timeout);
    window.removeEventListener("touchstart", this.resetInactivityTimer);
  },
  data() {
    return {
      beep: new Audio('https://resource.pafcpic.net/assets/pia/sounds/papsi_beep.mp3'),
      inactivity_timeout: null,
      selected_input_type: "account", // Default to Account Number
      input_value: '',
      account: '',
      serial: '',
      keyboard_visible: false, // Track keyboard visibility
      tab_options: [
        { value: "account", label: "Account Number" },
        { value: "serial", label: "Serial Number" },
      ],
      max_lengths: {
        account: 6,
        serial: 7,
      },
    };
  },

  inject: ['setLoading', 'setError'],
  methods: {
    playBeep() {
      this.beep.play();
    },
    resetInactivityTimer() {
      clearTimeout(this.inactivity_timeout);
      this.startInactivityTimer();
    },
    startInactivityTimer() {
      if (process.env.VUE_APP_DEVELOPMENT_MODE === 'false') {
        this.inactivity_timeout = setTimeout(() => {
        this.$router.push({ name: "start-page" });
        }, 30000);
      } 
      
    },
    typing(object) {
      this[object.name] = object.value;
    },
    openKeyboard(type) {
      this.selected_input_type = type;
      this.keyboard_visible = true;
    },
    closeKeyboard() {
      this.keyboard_visible = false;
    },
    updateField(value) {
      if (this.selected_input_type === 'account') {
        this.account = value;
      } else {
        this.serial = value;
      }
      this.input_value = value; // Sync the input value
      // this.closeKeyboard();
      this.handleProceed();
    },
    setSelectedTab(type) {
      this.selected_input_type = type;
      this.input_value = ''; // Reset the input value when switching tabs
    },
    filterAlphanumeric(event) {
      const input = event.target.value;
      // Allow only alphanumeric characters
      const filtered = input.replace(/[^A-Za-z0-9]/g, '');
      this.input_value = filtered;
    },
    handleProceed() {
      this.playBeep();
      const value = this.selected_input_type === "account" ? this.account : this.serial;
      this.setLoading(true);
      const validateMethod = this.selected_input_type === "account" ? user.validateAccount : user.validateSerial;
      const validationType = this.selected_input_type;

      validateMethod(value, validationType === "account" ? 3 : 4)
        .then((response) => {
          const { hash, uuid } = response.data.data;
          this.setLoading(false);
          this.$store.commit('setLoginParams', { hash, value, uuid, type: validationType });
          this.$router.push({ name: 'enter-pin-page' });
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.savePINEnrollee(error.response.data.data, this.input_value, this.selected_input_type === "account" ? 3 : 4);
            this.$router.push({ name: 'enroll-pin-page' });
          } else {
            this.showError(error.response.data.message);
          }
          this.setLoading(false);
        });
    },
  },
  components: {
    SidebarSection,
    ContentSection,
    FooterSection,
    LabelControl,
    IconControl,
    // ButtonControl,
    KeyboardControl,
  },
  computed: {
    isButtonDisabled() {
      const valueToValidate = this.selected_input_type === 'account' ? this.account : this.serial;
      const isSixAlphanumeric = /^[A-Za-z0-9]{6}$/.test(valueToValidate);
      return !isSixAlphanumeric;
    },
  },
};
</script>

<style scoped>
.papsi-mascot {
  bottom: 0;
  left: 0;
  width: 25rem;
}

.papsi-container {
  border-radius: 0 1rem 1rem 1rem;
}

.tab-active {
  border-bottom: 4px solid var(--primary-color);
  color: var(--primary-color);
}

.tab-inactive {
  color: gray;
}

@media (min-width: 2500px) {
  .papsi-container-acct-serial {
    height: auto !important;
  }

}
</style>
